import { type FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import useMeasure from 'react-use-measure';
import AlertIcon from '@assets/img/icons/alert.svg?react';
import { AppRoute } from '@common/app/routes';
import { type TabBarItemsType } from '@common/types/components';
import { StringXSmall } from '@components/Typography/Typography';
import cn from 'classnames';

import { gameItemOrder } from '@/common/constants';
import { measureToCssVars } from '@/common/lib/measure-to-css-vars';
import { selectIsGameItemHasOverflow } from '@/redux/selectors/userSelectors';
import { useAppSelector } from '@/redux/store';

import { NavigateIcon } from './NavigateIcon';

import css from './styles.module.scss';

interface NavigateItemProps {
  label: TabBarItemsType;
  index: number;
  highlightedIndex: number | null;
}

const NavigateItem: FC<NavigateItemProps> = ({
  label,
  index,
  highlightedIndex,
}) => {
  const [stockRef, rect] = useMeasure();
  const { t } = useTranslation();
  const highlightName = useAppSelector((state) => state.app.tabBarHighlight);
  const someResourceHasOverflow = useAppSelector((state) =>
    selectIsGameItemHasOverflow(state, gameItemOrder.resource),
  );
  const someMaterialHasOverflow = useAppSelector((state) =>
    selectIsGameItemHasOverflow(state, gameItemOrder.material),
  );
  const someGoodsHasOverflow = useAppSelector((state) =>
    selectIsGameItemHasOverflow(state, gameItemOrder.goods),
  );
  const userLevel = useAppSelector((state) => state.user.user?.level) ?? 0;
  const isStockDisabled = label === 'Stock' && userLevel < 2;

  useEffect(() => {
    if (label === 'Stock') {
      measureToCssVars(rect, '--tabbar-stock');
    }

    if (label === 'Office') {
      measureToCssVars(rect, '--tabbar-office');
    }
  }, [rect, label]);
  const navLink: Record<TabBarItemsType, string> = {
    Resources: AppRoute.RESOURCES,
    Materials: AppRoute.FACTORY,
    Goods: AppRoute.WORKSHOP,
    Office: AppRoute.MAIN,
    Stock: AppRoute.STOCK,
  };

  const highlight = index === highlightedIndex;

  return (
    <NavLink
      ref={stockRef}
      className={cn(
        'flex-1 relative',
        isStockDisabled && 'opacity-40 pointer-events-none',
      )}
      to={navLink[label]}
      aria-disabled={isStockDisabled}
      tabIndex={isStockDisabled ? -1 : undefined}
      onClick={(e): void => {
        if (isStockDisabled) e.preventDefault();
      }}
    >
      {({ isActive }) => (
        <>
          <div className={cn(css.navItem)}>
            <NavigateIcon
              label={label}
              isActive={isActive || highlightName === label}
              onboardingOverflow={highlight}
            />
            <StringXSmall
              className={cn(
                '!text-xs relative z-10 ',
                isActive || highlight || highlightName === label
                  ? '!text-accent'
                  : '!text-white/40',
                (highlightedIndex || highlightName) &&
                  'transition-colors duration-500',
              )}
              weight="medium"
            >
              {t(label)}
            </StringXSmall>
          </div>
          {(someResourceHasOverflow ||
            someMaterialHasOverflow ||
            someGoodsHasOverflow) &&
            label === 'Stock' && (
              <AlertIcon className="absolute top-2 right-6 w-4 h-4" />
            )}
        </>
      )}
    </NavLink>
  );
};

export { NavigateItem };
